import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Sidebar.css';
import AppIcon from './icons/AppIcon';

const Sidebar = ({ onLogout, onTabChange = () => {} }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarWidthPx, setSidebarWidthPx] = useState(170); // largura inicial ajustada
  const [isResizing, setIsResizing] = useState(false);
  // Estado de colapso do sidebar (usado em condicionais de renderização)
  const [isCollapsed, setIsCollapsed] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(170);

  // Atualiza a aba ativa quando a rota muda
  useEffect(() => {
    const path = location.pathname.split('/')[1]; // Pega o primeiro segmento da rota
    if (path) {
      setActiveTab(path);
    } else {
      setActiveTab('dashboard');
    }
  }, [location]);

  // Carregar largura salva do sidebar
  useEffect(() => {
    try {
      const raw = localStorage.getItem('app_sidebar_width_px');
      if (raw) {
        const val = Number(raw);
        if (Number.isFinite(val)) setSidebarWidthPx(Math.max(140, Math.min(360, val)));
      }
    } catch (_) {}
  }, []);

  // Handlers de redimensionamento
  const onResizeMouseDown = (e) => {
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = sidebarWidthPx;
    window.addEventListener('mousemove', onResizeMouseMove);
    window.addEventListener('mouseup', onResizeMouseUp);
  };

  const onResizeMouseMove = (e) => {
    try {
      const dx = e.clientX - startXRef.current;
      const next = Math.max(140, Math.min(360, startWidthRef.current + dx));
      setSidebarWidthPx(next);
    } catch (_) {}
  };

  const onResizeMouseUp = () => {
    setIsResizing(false);
    window.removeEventListener('mousemove', onResizeMouseMove);
    window.removeEventListener('mouseup', onResizeMouseUp);
    try { localStorage.setItem('app_sidebar_width_px', String(sidebarWidthPx)); } catch (_) {}
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  // Removido carregamento de dados do usuário de serviços legados

  // Ícones Material Symbols para sistema de atendimento e IA
  const menuItems = [
    {
      id: 'dashboard',
      icon: 'home',
      label: 'Página Principal',
      path: '/dashboard'
    },
    {
      id: 'atendimentos',
      icon: 'chat',
      label: 'Atendimentos',
      path: '/atendimentos'
    },
    {
      id: 'conexoes',
      icon: 'link',
      label: 'Conexões',
      path: '/conexoes'
    },
    {
      id: 'contatos',
      icon: 'contacts',
      label: 'Contatos',
      path: '/contatos'
    },
    {
      id: 'astro-agentes',
      icon: 'smart_toy',
      label: 'Astro Agentes',
      path: '/astro-agentes'
    },
    // IMPORTANTE: Os nomes foram invertidos intencionalmente
    // - "Agentes Halo" (label) → /meus-agentes (rota) = Página de CONFIGURAÇÃO/CRIAÇÃO de agentes
    // - "Meus Agentes" (label) → /agentes-halo (rota) = Página de CHAT com os agentes
    {
      id: 'meus-agentes',
      icon: 'group',
      label: 'Agentes Halo',
      path: '/meus-agentes'
    },
    {
      id: 'agentes-halo',
      icon: 'chat',
      label: 'Meus Agentes',
      path: '/agentes-halo',
      adminOnly: true
    },
    {
      id: 'projetos',
      icon: 'dashboard',
      label: 'Projetos',
      path: '/projetos'
    },
    {
      id: 'calendario',
      icon: 'calendar_month',
      label: 'Calendário',
      path: '/calendario'
    },
    {
      id: 'minha-empresa',
      icon: 'business',
      label: 'Minha Empresa',
      path: '/minha-empresa'
    },
    {
      id: 'configuracoes',
      icon: 'settings',
      label: 'Configurações',
      path: '/configuracoes'
    },
    {
      id: 'painel-admin',
      icon: 'admin_panel_settings',
      label: 'Painel Admin',
      path: '/painel-admin',
      adminOnly: true // Marcador para exibir apenas para admins
    }
  ];

  // Verificar se é admin
  const isAdmin = user?.plano_conta === 'admin' || user?.is_superuser === true;
  
  // Oculta temporariamente a aba "Astro Agentes" e filtra itens adminOnly
  const itemsToRender = menuItems.filter((item) => {
    if (item.id === 'astro-agentes') return false;
    if (item.adminOnly && !isAdmin) return false;
    return true;
  });

  return (
    <div
      className="sidebar"
      style={{ width: `${sidebarWidthPx}px` }}
    >
      <div className="sidebar-header" onClick={() => onTabChange('dashboard')}>
        <img
          src="/assets/images/logo-halo-fundo-trasparente.png"
          alt="HALO IA"
          className="sidebar-logo"
        />
        <span className="sidebar-brand-text">HALO IA</span>
      </div>
      
      <nav className="sidebar-nav">
        <ul className="nav-list">
          {itemsToRender.map((item, index) => {
            if (item.type === 'divider') {
              return <li key={`divider-${index}`} className="nav-divider"></li>;
            }
            
            if (item.type === 'section') {
              return (
                <React.Fragment key={`section-${item.label}`}>
                  {!isCollapsed && (
                    <li className="nav-section">
                      <span className="nav-section-text">{item.label}</span>
                    </li>
                  )}
                  {item.items.map((subItem) => (
                    <Link 
                      to={subItem.path} 
                      key={subItem.id} 
                      style={{ textDecoration: 'none' }}
                      onClick={() => {}}
                    >
                      <li 
                        className={`nav-item ${activeTab === subItem.id ? 'active' : ''}`}
                        data-tooltip={subItem.label}
                      >
                        <AppIcon name={subItem.icon} className="nav-icon" />
                        {!isCollapsed && <span className="nav-text">{subItem.label}</span>}
                      </li>
                    </Link>
                  ))}
                </React.Fragment>
              );
            }
            
            return (
              <Link 
                to={item.path}
                key={item.id} 
                style={{ textDecoration: 'none' }}
                onClick={() => {}}
              >
                <li 
                  className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                  data-tooltip={item.label}
                >
                  <AppIcon name={item.icon} className="nav-icon" />
                  <span className="nav-text">{item.label}</span>
                </li>
              </Link>
            );
          })}
        </ul>
      </nav>
      {/* Resizer do sidebar (direita) */}
      <div
        className="sidebar-resizer"
        onMouseDown={onResizeMouseDown}
        title="Arraste para redimensionar"
        aria-label="Redimensionar sidebar"
      />
    </div>
  );
};

export default Sidebar;
